import React, { useState, useEffect } from "react";
import { usePermissionContext } from "../../contexts/permission-context";
import Filter from "../../components/Filter/Filter";
import {
    fetchAPIData,
    generateHiddenColumns,
    copyToClipboard,
    hasPermission,
    assetsPermissionOptions,
    platformUseManageOptions,
    createFilterOptions, createSortFieldOptions
} from '../../Utils/Common';
import PreviewIcon from '@mui/icons-material/Preview';
import CopyIcon from '@mui/icons-material/AddToPhotosOutlined';
import IconButton from '@mui/material/IconButton';
import Table, { renderCellExpand } from '../../components/Table/Table';
import { GridActionsCellItem } from "@mui/x-data-grid";
import Divider from '@mui/material/Divider';
import Tooltip from '@mui/material/Tooltip';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormControl from '@mui/material/FormControl';
import HtmlTooltip from "../../components/HtmlToolTip/HtmlToolTip";
import ActionDialog from "../../components/Dialogs/ActionDialog";
import IngestionJobDialog from './IngestionJobDialog';
import { FormDialog } from "../../components/FormDialog/FormDialog";
import { usePersistentStorage } from "../../Utils/usePersistentStorage";
import "../pages.scss";

const ingestionStatusTaskSchemaView = require("../../json/ingestionStatusTaskSchema.json").view;
const ingestionStatusFileSchemaView = require("../../json/ingestionStatusFileSchema.json").view;

const uiSchema = {
    "metadataFiles": {
        "ui:options": {
            orderable: false,
            addable: false,
            removable: false
        }
    },
    "invalidDocumentPaths": {
        "ui:options": {
            orderable: false,
            addable: false,
            removable: false
        }
    },
    "failedCreatedDocumentPaths": {
        "ui:options": {
            orderable: false,
            addable: false,
            removable: false
        }
    },
    "failedUploadedDocumentPaths": {
        "ui:options": {
            orderable: false,
            addable: false,
            removable: false
        }
    },
    "trkFiles": {
        "ui:options": {
            orderable: false,
            addable: false,
            removable: false
        }
    },
    "internalTrakingFiles": {
        "ui:options": {
            orderable: false,
            addable: false,
            removable: false
        }
    },
    "emails": {
        "ui:options": {
            orderable: false,
            addable: false,
            removable: false
        }
    },
    "jobs": {
        "ui:options": {
            orderable: false,
            addable: false,
            removable: false
        },
        "items": {
            "metadataFiles": {
                "ui:options": {
                    orderable: false,
                    addable: false,
                    removable: false
                }
            },
            "invalidDocumentPaths": {
                "ui:options": {
                    orderable: false,
                    addable: false,
                    removable: false
                }
            },
            "failedCreatedDocumentPaths": {
                "ui:options": {
                    orderable: false,
                    addable: false,
                    removable: false
                }
            },
            "failedUploadedDocumentPaths": {
                "ui:options": {
                    orderable: false,
                    addable: false,
                    removable: false
                }
            },
            "trkFiles": {
                "ui:options": {
                    orderable: false,
                    addable: false,
                    removable: false
                }
            },
            "internalTrakingFiles": {
                "ui:options": {
                    orderable: false,
                    addable: false,
                    removable: false
                }
            },
            "emails": {
                "ui:options": {
                    orderable: false,
                    addable: false,
                    removable: false
                }
            }
        }
    }
};


const DocumentIngestionStatusTabPanel = ({ setNotify }) => {
    const { permissions } = usePermissionContext();
    const [ingestionStatusType, setIngestionStatusType] = useState('Task');

    const [taskApiData, setTaskApiData] = useState([]);
    const [fileApiData, setFileApiData] = useState([]);

    const [jobData, setJobData] = useState([]);
    const [rowJsonData, setRowJsonData] = useState({});
    const [open, setOpen] = useState(false);
    const [mode, setMode] = useState('view');  // default to view mode
    const [openJobDialog, setOpenJobDialog] = useState(false);

    const [reload, setReload] = useState(false);
    const [isLoading, setIsloading] = useState(false);

    const INGESTION_FILE_TYPE = 'File';
    const INGESTION_TASK_TYPE = 'Task';

    // API URL is dynamic in that COMPANY_ID is replaced
    const taskApiURL = '/api/companies/%COMPANY_ID%/ingestionTasks?';
    const fileApiURL = '/api/companies/%COMPANY_ID%/ingestionTasks/files?';

    const taskTotalLabel = "Matching Ingestion Tasks";
    const fileTotalLabel = "Matching Ingestion Files";

    const taskFieldData = [
        'connectorConfigId', 'ingestionTaskId', 'description', 'ingestionMode',
        'createdDate', 'modifiedDate', 'startTime', 'finishTime', 'jobs.batchId'
    ];

    const fileFieldData = [
        'jobId', 'ingestionTaskId', 'entityId', 'documentGUID', 'batchId', 'ingestionMode',
        'createdDate', 'modifiedDate', 'startTime', 'finishTime'
    ];

    const hasPlatformManage = hasPermission(permissions, platformUseManageOptions);

    const [ingestionTaskStatus, setIngestionTaskStatus] = usePersistentStorage("ingestionTaskStatus", []);
    const [ingestionFileStatus, setIngestionFileStatus] = usePersistentStorage("ingestionFileStatus", []);

    useEffect(() => {
        if (ingestionStatusType === INGESTION_TASK_TYPE) {
            setTaskApiData(ingestionTaskStatus);
        } else if (ingestionStatusType === INGESTION_FILE_TYPE) {
            setFileApiData(ingestionFileStatus);
        } else {
            console.log("Unknown ingestion status type: ", ingestionStatusType);
        }
    }, [ingestionStatusType]);

    const taskSortFieldData = [
        // {
        //     field: 'companyId',
        //     label: 'Company ID'
        // },
        {
            field: 'connectorConfigId',
            label: 'Connector Config ID'
        },
        {
            field: 'ingestionTaskId',
            label: 'Ingestion Task ID'
        },
        {
            field: 'description',
            label: 'Description'
        },
        {
            field: 'ingestionMode',
            label: 'Ingestion Mode'
        },
        {
            field: 'createdDate',
            label: 'Created Date'
        },
        {
            field: 'modifiedDate',
            label: 'Modified Date'
        },
        {
            field: 'startTime',
            label: 'Start Time'
        },
        {
            field: 'finishTime',
            label: 'Finish Time'
        },
        {
            field: 'jobs.batchId',
            label: 'Jobs Batch ID'
        },

    ];

    const fileSortFieldData = [
        // {
        //     field: 'companyId',
        //     label: 'Company ID'
        // },
        {
            field: 'entityId',
            label: 'Entity ID'
        },
        {
            field: 'jobId',
            label: 'Job ID'
        },
        {
            field: 'batchId',
            label: 'Batch ID'
        },
        {
            field: 'documentGUID',
            label: 'Document GUID'
        },

        {
            field: 'ingestionTaskId',
            label: 'Ingestion Task ID'
        },
        {
            field: 'ingestionMode',
            label: 'Ingestion Mode'
        },
        {
            field: 'createdDate',
            label: 'Created Date'
        },
        {
            field: 'modifiedDate',
            label: 'Modified Date'
        },
        {
            field: 'startTime',
            label: 'Start Time'
        },
        {
            field: 'finishTime',
            label: 'Finish Time'
        }
    ];

    const taskColumns = [
        // {
        //     field: 'id',
        //     type: 'number',
        //     headerName: 'ID',
        //     hide: true,
        //     minWidth: 50,
        //     flex: 1,
        //     headerAlign: 'left',
        //     align: 'left',
        // },
        {
            field: 'actions',
            type: 'actions',
            headerName: 'Actions',
            hideable:false,
            minWidth: 100,
            maxWidth: 100,
            flex: 1,
            headerAlign: 'left',
            align: 'left',
            getActions: (params) => getActionButtons(params)
        },
        {
            field: 'companyId',
            headerName: 'Company ID',
            hideable: false,
            type: 'number',
            minWidth: 100,
            maxWidth: 100,
            flex: 1,
            headerAlign: 'left',
            align: 'left',
            valueFormatter: (params) => {
                return params.value.toString();
            }
        },
        {
            field: 'connectorConfigId',
            hideable: false,
            headerName: 'Connector Config ID',
            minWidth: 290,
            flex: 1,
            headerAlign: 'left',
            align: 'left',
            renderCell: renderCellExpand
        },
        {
            field: 'ingestionTaskId',
            hideable: false,
            headerName: 'Ingestion Task ID',
            minWidth: 350,
            flex: 1,
            headerAlign: 'left',
            align: 'left',
            renderCell: (params) => {
                return (
                    <div className="guidContent">
                        <div style={{ width: '300px' }}>
                            {params.value}
                        </div>
                        {params.value &&
                            <div>
                                <Tooltip title="Copy to clipboard" arrow placement="right">
                                    <IconButton aria-label="copy ingestion task ID" onClick={(event) => copyToClipboard(event, params.row.ingestionTaskId)}>
                                        <CopyIcon sx={{ fontSize: 'small' }} />
                                    </IconButton>
                                </Tooltip>
                            </div>
                        }
                    </div>
                )
            }
        },
        {
            field: 'description',
            hideable: false,
            headerName: 'Description',
            minWidth: 150,
            maxWidth: 150,
            flex: 1,
            headerAlign: 'left',
            align: 'left',
            renderCell: renderCellExpand,
        },
        {
            field: 'ingestionMode',
            hideable: false,
            headerName: 'Ingestion Mode',
            minWidth: 125,
            maxWidth: 125,
            flex: 1,
            headerAlign: 'left',
            align: 'left',
            renderCell: renderCellExpand,
        },
        {
            field: 'startTime',
            hideable: false,
            headerName: 'Start Time',
            minWidth: 275,
            flex: 1,
            headerAlign: 'left',
            align: 'left',
            valueFormatter: (params) => {
                let formattedStartTime = "";
                if (params.value > 0) {
                    formattedStartTime = new Date(params.value).toISOString();
                }
                return formattedStartTime;
            },
            valueParser: (value) => {
                const date = new Date(value);
                return date.getTime();
            }
        },
        {
            field: 'finishTime',
            hideable: false,
            headerName: 'Finish Time',
            minWidth: 275,
            flex: 1,
            headerAlign: 'left',
            align: 'left',
            valueFormatter: (params) => {
                let formattedFinishTime = "";
                if (params.value > 0) {
                    formattedFinishTime = new Date(params.value).toISOString();
                }
                return formattedFinishTime;
            },
            valueParser: (value) => {
                const date = new Date(value);
                return date.getTime();
            }
        },
        {
            field: 'totalSizeKB',
            hideable: false,
            headerName: 'Total Size (KB)',
            minWidth: 125,
            maxWidth: 125,
            flex: 1,
            headerAlign: 'left',
            align: 'left',
            renderCell: renderCellExpand,
        },
        {
            field: 'documentFailurePaths',
            hideable: false,
            headerName: 'Document Failure Paths',
            minWidth: 350,
            flex: 1,
            headerAlign: 'left',
            align: 'left',
            renderCell: (params) => {

                return (
                    <div style={{ flexDirection: 'column', display: 'inline-block', textAlign: 'left', wordBreak: 'break-word' }}>

                        {params.row.invalidDocumentPaths &&
                            params.row.invalidDocumentPaths.length >= 1 &&
                            <>&nbsp;<strong>Invalid Document Paths: </strong><br />

                                {params.row.invalidDocumentPaths.map((item) => {
                                    return (
                                        <>
                                            &nbsp;&nbsp;{item}<br />
                                        </>
                                    );
                                })}
                            </>
                        }

                        {params.row.failedCreatedDocumentPaths &&
                            params.row.failedCreatedDocumentPaths.length >= 1 &&
                            <>&nbsp;<strong>Failed Created Paths: </strong><br />

                                {params.row.failedCreatedDocumentPaths.map((item) => {
                                    return (
                                        <>
                                            &nbsp;&nbsp;{item}<br />
                                        </>
                                    );
                                })}
                            </>
                        }

                        {params.row.failedUploadedDocumentPaths &&
                            params.row.failedUploadedDocumentPaths.length >= 1 &&
                            <>&nbsp;<strong>Failed Upload Document Paths: </strong><br />

                                {params.row.failedUploadedDocumentPaths.map((item) => {
                                    return (
                                        <>
                                            &nbsp;&nbsp;{item}<br />
                                        </>
                                    );
                                })}
                            </>
                        }

                    </div>
                );
            }
        },
        {
            field: 'metadataFiles',
            hideable: false,
            headerName: 'Metadata Files',
            minWidth: 325,
            flex: 1,
            headerAlign: 'left',
            align: 'left',
            renderCell: (params) => {

                return (
                    <>
                        {params.row.metadataFiles && params.row.metadataFiles.length >= 1 && (
                            <div style={{ flexDirection: 'column', display: 'inline-block' }}>
                                <table border='1' style={{ borderCollapse: 'collapse', width: '300px' }}>
                                    <tr>
                                        <th style={{ textAlign: 'left' }}>Metadata File Path</th>
                                    </tr>
                                    {params.row.metadataFiles && params.row.metadataFiles.map((item, index) => {
                                        return (
                                            <tr key={index}>
                                                <td style={{ padding: '2px', wordBreak: 'break-word' }}>
                                                    {item}
                                                </td>
                                            </tr>
                                        );
                                    })
                                    }
                                </table>
                            </div>
                        )}
                    </>
                );
            }
        },
        {
            field: 'trkFiles',
            hideable: false,
            headerName: 'TRKFiles',
            minWidth: 325,
            flex: 1,
            headerAlign: 'left',
            align: 'left',
            renderCell: (params) => {

                return (
                    <>
                        {params.row.trkFiles && params.row.trkFiles.length >= 1 && (
                            <div>
                                <p>
                                    <HtmlTooltip followCursor placement="top" title={
                                        <>
                                            <h4 style={{ backgroundColor: '#E5E4E2' }}>Tracking Files Values</h4>
                                            <table border='1' style={{ borderCollapse: 'collapse', width: '450px' }}>
                                                <tr>
                                                    <th style={{ textAlign: 'left' }}>FilePath</th>
                                                    <th style={{ textAlign: 'left' }}>Created Date</th>
                                                </tr>
                                                {params.row.trkFiles && params.row.trkFiles.map((trkFiles, index) => {
                                                    return (
                                                        <tr key={index}>
                                                            <td nowrap style={{ padding: '2px' }}>
                                                                {trkFiles.filePath}
                                                            </td>
                                                            <td nowrap style={{ padding: '2px' }}>
                                                                {trkFiles.createdDate}
                                                            </td>
                                                        </tr>
                                                    );
                                                })}
                                            </table>
                                        </>
                                    }>
                                        <div style={{ flexDirection: 'column', display: 'inline-block' }}>
                                            <table border='1' style={{ borderCollapse: 'collapse', width: '150px' }}>
                                                <tr>
                                                    <th style={{ textAlign: 'left' }}>FilePath</th>
                                                </tr>
                                                {params.row.trkFiles && params.row.trkFiles.map((trkFiles, index) => {
                                                    return (
                                                        <tr key={index}>
                                                            <td nowrap style={{ padding: '2px' }}>
                                                                {trkFiles.filePath}
                                                            </td>
                                                        </tr>
                                                    );
                                                })
                                                }
                                            </table>
                                        </div>

                                    </HtmlTooltip>

                                </p>
                            </div>

                        )}
                    </>
                );
            }
        },
        {
            field: 'internalTrakingFiles',
            hideable: false,
            headerName: 'Internal Tracking Files',
            minWidth: 300,
            flex: 1,
            headerAlign: 'left',
            align: 'left',
            renderCell: (params) => {

                return (
                    <>
                        {params.row.internalTrakingFiles && params.row.internalTrakingFiles.length >= 1 && (
                            <div>
                                <p>
                                    <HtmlTooltip followCursor placement="top" title={
                                        <>
                                            <h4 style={{ backgroundColor: '#E5E4E2' }}>Internal Tracking Files Values</h4>
                                            <table border='1' style={{ borderCollapse: 'collapse', width: '450px' }}>
                                                <tr>
                                                    <th style={{ textAlign: 'left' }}>FilePath</th>
                                                    <th style={{ textAlign: 'left' }}>Created Date</th>
                                                </tr>
                                                {params.row.internalTrakingFiles && params.row.internalTrakingFiles.map((intTrkFiles, index) => {
                                                    return (
                                                        <tr key={index}>
                                                            <td nowrap style={{ padding: '2px' }}>
                                                                {intTrkFiles.filePath}
                                                            </td>
                                                            <td nowrap style={{ padding: '2px' }}>
                                                                {intTrkFiles.createdDate}
                                                            </td>
                                                        </tr>
                                                    );
                                                })}
                                            </table>
                                        </>
                                    }>
                                        <div style={{ flexDirection: 'column', display: 'inline-block' }}>
                                            {params.row.internalTrakingFiles && params.row.internalTrakingFiles.map((intTrkFiles, index) => {
                                                return (
                                                    <>{intTrkFiles.filePath}<br /></>
                                                );
                                            })
                                            }
                                        </div>

                                    </HtmlTooltip>

                                </p>
                            </div>

                        )}
                    </>
                );
            }
        },
        {
            field: 'emails',
            hideable: false,
            headerName: 'Emails',
            minWidth: 325,
            flex: 1,
            headerAlign: 'left',
            align: 'left',
            renderCell: (params) => {

                return (
                    <>
                        {params.row.emails && params.row.emails.length >= 1 && (
                            <div>
                                <p>
                                    <HtmlTooltip followCursor placement="top" title={
                                        <>
                                            <h4 style={{ backgroundColor: '#E5E4E2' }}>Email Values</h4>
                                            <table border='1' style={{ borderCollapse: 'collapse', width: '450px' }}>
                                                <tr>
                                                    <th style={{ textAlign: 'left' }}>Recipents</th>
                                                    <th style={{ textAlign: 'left' }}>Sent Date</th>
                                                </tr>
                                                {params.row.emails && params.row.emails.map((emailData, index) => {
                                                    return (
                                                        <tr key={index}>
                                                            <td nowrap style={{ padding: '2px' }}>
                                                                {emailData.recipents}
                                                            </td>
                                                            <td nowrap style={{ padding: '2px' }}>
                                                                {emailData.sentDate}
                                                            </td>
                                                        </tr>
                                                    );
                                                })}
                                            </table>
                                        </>
                                    }>

                                        <div style={{ flexDirection: 'column', display: 'inline-block' }}>
                                            <table border='1' style={{ borderCollapse: 'collapse', width: '150px' }}>
                                                <tr>
                                                    <th style={{ textAlign: 'left' }}>Recipents</th>
                                                </tr>
                                                {params.row.emails && params.row.emails.map((emailData, index) => {
                                                    return (
                                                        <tr key={index}>
                                                            <td nowrap style={{ padding: '2px' }}>
                                                                {emailData.recipents}
                                                            </td>
                                                        </tr>
                                                    );
                                                })
                                                }
                                            </table>
                                        </div>

                                    </HtmlTooltip>

                                </p>
                            </div>

                        )}
                    </>
                );
            }
        },

        {
            field: 'processedCounts',
            hideable: false,
            headerName: 'Processed Counts',
            minWidth: 250,
            flex: 1,
            headerAlign: 'left',
            align: 'left',
            renderCell: (params) => {

                return (
                    <div style={{ flexDirection: 'column', display: 'inline-block', textAlign: 'left' }}>

                        <>&nbsp;<strong>Valid Document Count: </strong>&nbsp;{params.row.validDocumentCount}</>

                        <><br />&nbsp;<strong>Created Document Count: </strong>&nbsp;{params.row.createdDocumentCount}</>

                        <><br />&nbsp;<strong>Original Document Count: </strong>&nbsp;{params.row.originalDocumentCount}</>

                        <><br />&nbsp;<strong>Uploaded Document Count: </strong>&nbsp;{params.row.uploadedDocumentCount}</>
                    </div>
                );
            }
        },
        {
            field: 'errorCounts',
            hideable: false,
            headerName: 'Error Counts',
            minWidth: 260,
            flex: 1,
            headerAlign: 'left',
            align: 'left',
            renderCell: (params) => {

                return (
                    <div style={{ flexDirection: 'column', display: 'inline-block', textAlign: 'left' }}>

                        <>&nbsp;<strong>Invalid Document Count: </strong>&nbsp;{params.row.invalidDocumentCount}</>

                        <><br />&nbsp;<strong>Failed Created Document Count: </strong>&nbsp;{params.row.failedCreatedDocumentCount}</>

                        <><br />&nbsp;<strong>Failed Uploaded Document Count: </strong>&nbsp;{params.row.failedUploadedDocumentCount}</>

                    </div>
                );
            }
        },
        {
            field: 'summaryCounts',
            hideable: false,
            headerName: 'Summary Counts',
            minWidth: 200,
            flex: 1,
            headerAlign: 'left',
            align: 'left',
            renderCell: (params) => {

                return (
                    <div style={{ flexDirection: 'column', display: 'inline-block', textAlign: 'left' }}>

                        <>&nbsp;<strong>Control File Count: </strong>&nbsp;{params.row.controlFileCount}</>

                        <><br />&nbsp;<strong>Metadata File Count: </strong>&nbsp;{params.row.metadataFileCount}</>

                        <><br />&nbsp;<strong>Completed Job Count: </strong>&nbsp;{params.row.completeJobCount}</>

                        <><br />&nbsp;<strong>Job Count: </strong>&nbsp;{params.row.jobCount}</>

                    </div>
                );
            }
        },
        {
            field: 'createdDate',
            headerName: 'Created Date',
            hide: true,
            minWidth: 225,
            flex: 1,
            headerAlign: 'left',
            align: 'left',
            renderCell: renderCellExpand,
        },
        {
            field: 'modifiedDate',
            headerName: 'Modified Date',
            hide: true,
            minWidth: 225,
            flex: 1,
            headerAlign: 'left',
            align: 'left',
            renderCell: renderCellExpand,
        }

    ];

    const fileColumns = [
        {
            field: 'companyId',
            headerName: 'Company ID',
            type: 'number',
            minWidth: 100,
            maxWidth: 100,
            flex: 1,
            headerAlign: 'left',
            align: 'left',
            valueFormatter: (params) => {
                return params.value.toString();
            }
        },
        {
            hideable: false,
            field: 'documentGUID',
            headerName: 'Document GUID',
            minWidth: 350,
            maxWidth: 350,
            flex: 1,
            headerAlign: 'left',
            align: 'left',
            renderCell: (params) => {
                return (
                    <div className="guidContent">
                        <div style={{ width: '300px' }}>
                            {params.value}
                        </div>
                        {params.value &&
                            <div>
                                <Tooltip title="Copy to clipboard" arrow placement="right">
                                    <IconButton aria-label="copy document GUID" onClick={(event) => copyToClipboard(event, params.row.documentGUID)}>
                                        <CopyIcon sx={{ fontSize: 'small' }} />
                                    </IconButton>
                                </Tooltip>
                            </div>
                        }
                    </div>
                )
            }
        },
        {
            hideable: false,
            field: 'ingestionTaskId',
            headerName: 'Ingestion Task ID',
            minWidth: 350,
            flex: 1,
            headerAlign: 'left',
            align: 'left',
            renderCell: (params) => {
                return (
                    <div className="guidContent">
                        <div style={{ width: '300px' }}>
                            {params.value}
                        </div>
                        {params.value &&
                            <div>
                                <Tooltip title="Copy to clipboard" arrow placement="right">
                                    <IconButton aria-label="copy ingestion task ID" onClick={(event) => copyToClipboard(event, params.row.ingestionTaskId)}>
                                        <CopyIcon sx={{ fontSize: 'small' }} />
                                    </IconButton>
                                </Tooltip>
                            </div>
                        }
                    </div>
                )
            }
        },

        {
            hideable: false,
            field: 'batchId',
            headerName: 'Batch ID',
            minWidth: 200,
            flex: 1,
            headerAlign: 'left',
            align: 'left',
            renderCell: (params) => {
                return (

                    <div className="guidContent">
                        <div style={{ width: '150px' }}>
                            {params.value}
                        </div>
                        {params.value &&
                            <div>
                                <Tooltip title="Copy to clipboard" arrow placement="right">
                                    <IconButton aria-label="copy batch ID" onClick={(event) => copyToClipboard(event, params.row.batchId)}>
                                        <CopyIcon sx={{ fontSize: 'small' }} />
                                    </IconButton>
                                </Tooltip>
                            </div>
                        }
                    </div>
                )
            }
        },
        {
            hideable: false,
            field: 'jobId',
            headerName: 'Job ID',
            minWidth: 350,
            flex: 1,
            headerAlign: 'left',
            align: 'left',
            renderCell: (params) => {
                return (
                    <div className="guidContent">
                        <div style={{ width: '300px' }}>
                            {params.value}
                        </div>
                        {params.value &&
                            <div>
                                <Tooltip title="Copy to clipboard" arrow placement="right">
                                    <IconButton aria-label="copy job ID" onClick={(event) => copyToClipboard(event, params.row.jobId)}>
                                        <CopyIcon sx={{ fontSize: 'small' }} />
                                    </IconButton>
                                </Tooltip>
                            </div>
                        }
                    </div>
                )
            }
        },

        {
            hideable: false,
            field: 'entityId',
            headerName: 'Entity ID',
            minWidth: 300,
            flex: 1,
            headerAlign: 'left',
            align: 'left',
            renderCell: renderCellExpand
        },

        {
            hideable: false,
            field: 'ingestionMode',
            headerName: 'Ingestion Mode',
            minWidth: 125,
            maxWidth: 125,
            flex: 1,
            headerAlign: 'left',
            align: 'left',
            renderCell: renderCellExpand,
        },

        {
            hideable: false,
            field: 'fullPath',
            headerName: ' Full Path',
            minWidth: 325,
            //maxWidth: 200,
            flex: 1,
            headerAlign: 'left',
            align: 'left',
            renderCell: (params) => {
                return (

                    <div className="guidContent">
                        <div style={{ width: '275px', wordBreak: 'break-word' }}>
                            {params.value}
                        </div>
                        {params.value &&
                            <div>
                                <Tooltip title="Copy to clipboard" arrow placement="right">
                                    <IconButton aria-label="copy path" onClick={(event) => copyToClipboard(event, params.row.fullPath)}>
                                        <CopyIcon sx={{ fontSize: 'small' }} />
                                    </IconButton>
                                </Tooltip>
                            </div>
                        }
                    </div>
                )
            }
        },
        {
            hideable: false,
            field: 'startTime',
            headerName: 'Start Time',
            minWidth: 275,
            flex: 1,
            headerAlign: 'left',
            align: 'left',
            valueFormatter: (params) => {
                let formattedStartTime = "";
                if (params.value > 0) {
                    formattedStartTime = new Date(params.value).toISOString();
                }
                return formattedStartTime;
            },
            valueParser: (value) => {
                const date = new Date(value);
                return date.getTime();
            }
        },
        {
            hideable: false,
            field: 'finishTime',
            headerName: 'Finish Time',
            minWidth: 275,
            flex: 1,
            headerAlign: 'left',
            align: 'left',
            valueFormatter: (params) => {
                let formattedFinishTime = "";
                if (params.value > 0) {
                    formattedFinishTime = new Date(params.value).toISOString();
                }
                return formattedFinishTime;
            },
            valueParser: (value) => {
                const date = new Date(value);
                return date.getTime();
            }
        },
        {
            field: 'totalSizeKB',
            hideable: false,
            headerName: 'Total Size (KB)',
            minWidth: 125,
            maxWidth: 125,
            flex: 1,
            headerAlign: 'left',
            align: 'left',
            renderCell: renderCellExpand,
        },

        {
            hideable: false,
            field: 'created',
            headerName: 'Created',
            type: 'boolean',
            minWidth: 75,
            maxWidth: 75,
            flex: 1,
            headerAlign: 'left',
            align: 'left',
            renderCell: (params) => {
                return (
                    <>
                        {params.value ? 'Y' : 'N'}
                    </>
                );
            }
        },

        {
            hideable: false,
            field: 'uploaded',
            headerName: 'Uploaded',
            type: 'boolean',
            minWidth: 80,
            maxWidth: 80,
            flex: 1,
            headerAlign: 'left',
            align: 'left',
            renderCell: (params) => {
                return (
                    <>
                        {params.value ? 'Y' : 'N'}
                    </>
                );
            }
        },
        {
            hideable: false,
            field: 'exceptionMsg',
            headerName: 'Exception Message',
            minWidth: 225,
            flex: 1,
            headerAlign: 'left',
            align: 'left',
            renderCell: (params) => {

                return (
                    <div style={{ flexDirection: 'column', display: 'inline-block', textAlign: 'left' }}>
                        {params.value}
                    </div>
                );
            }
        },

        {
            field: 'createdDate',
            headerName: 'Created Date',
            hide: true,
            minWidth: 225,
            flex: 1,
            headerAlign: 'left',
            align: 'left',
            renderCell: renderCellExpand,
        },
        {
            field: 'modifiedDate',
            headerName: 'Modified Date',
            hide: true,
            minWidth: 225,
            flex: 1,
            headerAlign: 'left',
            align: 'left',
            renderCell: renderCellExpand,
        }

    ];

    const getActionButtons = (params) => {
        let actionButtons = [];

        let viewJobButton = <GridActionsCellItem
            icon={
                <Tooltip title="Display Ingestion Task Jobs" arrow placement="right">
                    <PreviewIcon />
                </Tooltip>
            }
            label='View Ingestion Task Jobs'
            onClick={() => {
                viewJobData(params.row.jobs);
            }
            }
            disabled={params.row.jobs && params.row.jobs.length === 0}
        />;

        hasPlatformManage && actionButtons.push(viewJobButton);

        return actionButtons;

    };

    // const getColumns = () => {
    //     return (hasPlatformManage && columns) || columns.filter(obj => { return obj.field !== "actions"});
    // };

    const toggleColumnPinning = () => {
        return (hasPlatformManage && 'columnPinning') || '';
    };

    const viewJobData = (jobs) => {

        setJobData(jobs);

        setOpenJobDialog(true);
    };

    const fetchData = async (apiURL2, qparams) => {
        //console.log('inside fetch customer data:', qparams);

        /*
        let message = "Loading customer data ...";
     
        setNotify({
            isOpen: true,
            message: message,
            severity: 'info'
        });
    */
        setIsloading(true);
        await fetchAPIData(apiURL2, qparams)
            .then((data) => {
                // console.log("Updating table data: ", data);
                if (ingestionStatusType === INGESTION_TASK_TYPE) {
                    setTaskApiData(data);
                    setIngestionTaskStatus(data);
                } else if (ingestionStatusType === INGESTION_FILE_TYPE) {
                    setFileApiData(data);
                    setIngestionFileStatus(data);
                }
                // clear the reload false now that data is loaded
                setReload(false);
                setIsloading(false);
            })
            .catch((err) => {
                console.log(err);

                let message = `Failed to load document ingestion ${ingestionStatusType.toLowerCase()} status data \n\n ${err.response.statusText} : ${err.response.data.message}`;
                setNotify({
                    isOpen: true,
                    alertTitle: 'Request failed',
                    message: message,
                    severity: 'error'
                });

                // clear data since user may have previously loaded data that is already displayed
                if (ingestionStatusType === INGESTION_TASK_TYPE) {
                    setTaskApiData([]);
                } else if (ingestionStatusType === INGESTION_FILE_TYPE) {
                    setFileApiData([]);
                }
                setReload(false);
                setIsloading(false);
            });
    };

    const viewTaskRowData = (param, event) => {

        // remove the id from table data as table data only not part of json stored
        const {
            id,
            ...JsonData
        } = param.row;

        let formattedData = JSON.parse(JSON.stringify(JsonData)); // make a deep copy to work on

        formatData(formattedData);

        setRowJsonData(formattedData);
        setMode('view');
        setOpen(true);
    };

    const viewFileRowData = (param, event) => {

        // remove the id from table data as table data only not part of json stored
        const {
            id,
            ...JsonData
        } = param.row;

        let formattedData = JSON.parse(JSON.stringify(JsonData)); // make a deep copy to work on

        formatFileData(formattedData);

        setRowJsonData(formattedData);
        setMode('view');
        setOpen(true);
    };

    const handleClose = () => {
        setOpen(false);
    };

    const handleJobDialogClose = () => {
        setOpenJobDialog(false);
    };

    const handleChange = (event) => {
        setIngestionStatusType(event.target.value);
    };

    const formatData = (data) => {
        // check if object is not empty
        if (Object.keys(data).length !== 0) {
            data.startTime = data.startTime > 0 ? new Date(data.startTime).toISOString() : "";
            data.finishTime = data.finishTime > 0 ? new Date(data.finishTime).toISOString() : "";

            data.jobs.forEach((obj) => {
                obj.startTime = obj.startTime > 0 ? new Date(obj.startTime).toISOString() : "";
                obj.finishTime = obj.finishTime > 0 ? new Date(obj.finishTime).toISOString() : "";
            });
        }
    };

    const formatFileData = (data) => {
        // check if object is not empty
        if (Object.keys(data).length !== 0) {
            data.startTime = data.startTime > 0 ? new Date(data.startTime).toISOString() : "";
            data.finishTime = data.finishTime > 0 ? new Date(data.finishTime).toISOString() : "";
        }
    };

    const getIngestionTypeTotalLabel = (ingestionStatusType) => {
        switch (ingestionStatusType) {
            case INGESTION_FILE_TYPE:
                return fileTotalLabel;
            case INGESTION_TASK_TYPE:
                return taskTotalLabel;
            default:
                // log in case someone add new types later
                console.log("unknown ingestion type: ", ingestionStatusType);
                break;
        }
    };

    const getIngestionTypeApiUrl = (ingestionStatusType) => {
        switch (ingestionStatusType) {
            case INGESTION_FILE_TYPE:
                return fileApiURL;
            case INGESTION_TASK_TYPE:
                return taskApiURL;
            default:
                // log in case someone add new types later
                console.log("unknown ingestion type: ", ingestionStatusType);
                break;
        }
    };

    const getIngestionTypeColumns = (ingestionStatusType) => {
        switch (ingestionStatusType) {
            case INGESTION_FILE_TYPE:
                return (hasPlatformManage && fileColumns) || fileColumns.filter(obj => { return obj.field !== "actions" });
            //return fileColumns;
            case INGESTION_TASK_TYPE:
                return (hasPlatformManage && taskColumns) || taskColumns.filter(obj => { return obj.field !== "actions" });
            //return taskColumns;
            default:
                // log in case someone add new types later
                console.log("unknown ingestion type: ", ingestionStatusType);
                break;
        }
    };

    const getIngestionTypeFieldData = (ingestionStatusType) => {
        switch (ingestionStatusType) {
            case INGESTION_FILE_TYPE:
                return fileFieldData;
            case INGESTION_TASK_TYPE:
                return taskFieldData;
            default:
                // log in case someone add new types later
                console.log("unknown ingestion type: ", ingestionStatusType);
                break;
        }
    };

    const getIngestionTypeSortFieldData = (ingestionStatusType) => {
        switch (ingestionStatusType) {
            case INGESTION_FILE_TYPE:
                return fileSortFieldData;
            case INGESTION_TASK_TYPE:
                return taskSortFieldData;
            default:
                // log in case someone add new types later
                console.log("unknown ingestion type: ", ingestionStatusType);
                break;
        }
    };

    const getIngestionTypeApiData = (ingestionStatusType) => {
        switch (ingestionStatusType) {
            case INGESTION_FILE_TYPE:
                return fileApiData;
            case INGESTION_TASK_TYPE:
                return taskApiData;
            default:
                // log in case someone add new types later
                console.log("unknown ingestion type: ", ingestionStatusType);
                break;
        }
    };

    const getIngestionTypeViewRowData = (ingestionStatusType) => {
        switch (ingestionStatusType) {
            case INGESTION_FILE_TYPE:
                return viewFileRowData;
            case INGESTION_TASK_TYPE:
                return viewTaskRowData;
            default:
                // log in case someone add new types later
                console.log("unknown ingestion type: ", ingestionStatusType);
                break;
        }
    };

    const getIngestionTypeViewSchema = (ingestionStatusType) => {
        switch (ingestionStatusType) {
            case INGESTION_FILE_TYPE:
                return ingestionStatusFileSchemaView
            case INGESTION_TASK_TYPE:
                return ingestionStatusTaskSchemaView;
            default:
                // log in case someone add new types later
                console.log("unknown ingestion type: ", ingestionStatusType);
                break;
        }
    };

    const getIngestionStatusSection = (ingestionStatusType) => {
        let columns = getIngestionTypeColumns(ingestionStatusType);
        let apiURL = getIngestionTypeApiUrl(ingestionStatusType);
        let fieldData = getIngestionTypeFieldData(ingestionStatusType);
        let sortFieldData = getIngestionTypeSortFieldData(ingestionStatusType);
        let totalLabel = getIngestionTypeTotalLabel(ingestionStatusType);
        let tabName = `documentIngestion${ingestionStatusType}Tab`;
        let apiData = getIngestionTypeApiData(ingestionStatusType);
        let viewRowData = getIngestionTypeViewRowData(ingestionStatusType);

        let section =
            <>
                <div id={`${ingestionStatusType}Section`}>

                    <div id={`ingestion${ingestionStatusType}StatusDocumentfilterContainer`} style={{ marginTop: '0px', marginLeft: '8px' }}>
                        <Filter permission={assetsPermissionOptions} fields={createFilterOptions(fieldData)} sortFields={createSortFieldOptions(sortFieldData)} apiURL={apiURL} fetchData={fetchData} setNotify={setNotify} reload={reload} useCompanyId={true} totalLabel={totalLabel} tabName={tabName} />
                    </div>


                    <Divider />

                    <div id={`ingestion${ingestionStatusType}StatusTableContainer`} className={ingestionStatusType === INGESTION_TASK_TYPE ? toggleColumnPinning() : ''}>
                        <div >
                            <Table data={apiData} columns={columns} hiddenColumns={generateHiddenColumns(columns)} viewRowData={viewRowData} isLoading={isLoading} />
                        </div>
                    </div>
                </div >
            </>;

        return section;
    };

    return (
        <>
            <div id="ingestionStatusTypeContainer" style={{ marginTop: '0px', marginLeft: '16px' }}>

                <div style={{ display: 'flex' }}>
                    <FormControl>
                        <RadioGroup
                            row
                            aria-labelledby="ingestion status type radio buttons"
                            name="ingestion-status-type-radio-buttons-group"
                            value={ingestionStatusType}
                            onChange={handleChange}
                        >
                            <FormControlLabel value={INGESTION_TASK_TYPE} control={<Radio size="small" />} label="Task" />
                            <FormControlLabel value={INGESTION_FILE_TYPE} control={<Radio size="small" />} label="File" />
                        </RadioGroup>
                    </FormControl>
                </div>

            </div>

            <Divider />

            {getIngestionStatusSection(ingestionStatusType)}

            {/* This dialog only support view. No need for form or save */}
            <ActionDialog
                open={open}
                mode={mode}
                onClose={handleClose}
                title={`Ingestion Status ${ingestionStatusType} Information`}
                defaultTab='JSON'
                propertiesTabContent={
                    <FormDialog
                        formId={`ingestionStatus${ingestionStatusType}DialogId`}
                        readOnly={true}
                        schema={getIngestionTypeViewSchema(ingestionStatusType)}
                        uiSchema={ingestionStatusType === INGESTION_TASK_TYPE ? uiSchema : undefined}
                        formData={rowJsonData}
                    />
                }
                jsonDataName={`ingestion status ${ingestionStatusType.toLowerCase()}s`}
                JSONTabContent={rowJsonData}
            />


            <IngestionJobDialog
                open={openJobDialog}
                onClose={handleJobDialogClose}
                data={jobData}
            />


        </>
    );
}

export default DocumentIngestionStatusTabPanel;